<template>
  <section class="final-review-shell">
    <div class="block-header block-header--stack-mobile">
      <div class="block-header__copy">
        <span class="block-eyebrow">Final Delivery</span>
        <h2>终版交付台</h2>
        <p class="block-tip">先验收成片，再处理终版音频，最后统一交付。</p>
      </div>
      <div class="block-header__status-card">
        <span>当前播放</span>
        <strong>{{ currentPlaybackLabel }}</strong>
        <small>{{ currentPlaybackStatusChip }}</small>
      </div>
    </div>

    <div class="final-stage-hero">
      <div class="final-stage-hero__media">
        <div class="final-review-player">
          <span class="final-review-player__badge">{{ currentPlaybackLabel }}</span>
          <video
            v-if="effectiveFinalVideoUrl"
            :key="effectiveFinalVideoUrl"
            :src="effectiveFinalVideoUrl"
            controls
            playsinline
            webkit-playsinline="true"
            x5-playsinline="true"
            crossorigin="anonymous"
            preload="metadata"
            @loadeddata="emit('final-video-loaded')"
            @error="emit('final-video-error')"
          />
          <div v-else class="final-review-player__placeholder">
            <strong>{{ finalStageStatusLabel }}</strong>
            <p>{{ finalStageVideoMessage }}</p>
          </div>
          <div v-if="finalVideoPlayerError" class="final-review-player__error">
            <strong>成片兼容提示</strong>
            <p>{{ finalVideoPlayerError }}</p>
          </div>
        </div>
      </div>
    </div>

    <section class="final-delivery-rail">
      <article class="delivery-step-card">
        <div class="delivery-step-card__head">
          <span class="delivery-step-card__step">Step 1</span>
          <strong>先确认系统成片</strong>
        </div>
        <p>{{ systemFinalVideoUrl ? '系统成片已经可播，所有声音处理都应建立在这版画面底板上。' : '当前还没有系统成片，先完成上游镜头生成与拼接。' }}</p>
        <div class="delivery-step-card__status">
          <span class="final-version-chip">{{ systemFinalVideoUrl ? '系统成片已就绪' : '系统成片未生成' }}</span>
          <span v-if="isSystemPlaybackActive" class="final-version-chip final-version-chip--accent">当前播放中</span>
        </div>
        <div v-if="systemFinalVideoUrl && preferredFinalVideoSource !== 'system'" class="delivery-step-card__actions">
          <el-button size="small" plain @click="emit('use-system-as-delivery')">切回系统成片</el-button>
        </div>
      </article>

      <article class="delivery-step-card">
        <div class="delivery-step-card__head">
          <span class="delivery-step-card__step">Step 2</span>
          <strong>再确认声音预览</strong>
        </div>
        <p>{{ previewSummaryText }}</p>
        <div class="delivery-step-card__status">
          <span class="final-version-chip">{{ audioMixPreviewVideo?.url ? '混音预览已生成' : '尚未生成预览' }}</span>
          <span v-if="isPreviewApplied" class="final-version-chip final-version-chip--accent">已应用到交付</span>
        </div>
        <div v-if="audioMixPreviewVideo?.url" class="delivery-step-card__actions">
          <el-button size="small" plain @click="emit('download-preview-video')">下载预览</el-button>
          <el-button v-if="!isPreviewApplied" size="small" type="primary" @click="emit('apply-preview-as-delivery')">设为声音交付版</el-button>
          <el-button v-else-if="preferredFinalVideoSource !== 'audio_mix_delivery'" size="small" type="primary" @click="emit('use-audio-delivery')">切到声音交付版</el-button>
        </div>
      </article>

      <article class="delivery-step-card delivery-step-card--accent">
        <div class="delivery-step-card__head">
          <span class="delivery-step-card__step">Step 3</span>
          <strong>最后指定交付版</strong>
        </div>
        <p>{{ deliverySummaryText }}</p>
        <div class="delivery-step-card__status">
          <span class="final-version-chip final-version-chip--accent">{{ currentPlaybackLabel }}</span>
          <span class="final-version-chip final-version-chip--soft">{{ currentPlaybackStatusChip }}</span>
        </div>
        <div v-if="deliveryActions.length" class="delivery-step-card__actions">
          <el-button
            v-for="action in deliveryActions"
            :key="action.key"
            size="small"
            :type="action.type"
            :plain="action.type !== 'primary'"
            @click="action.onClick()"
          >
            {{ action.label }}
          </el-button>
        </div>
      </article>
    </section>

    <div class="final-review-workbench">
      <FinalAudioStudioCard
        :final-voiceover-mode="finalVoiceoverMode"
        :final-voiceover-tone="finalVoiceoverTone"
        :final-bgm-plan="finalBgmPlan"
        :final-voiceover-mode-options="finalVoiceoverModeOptions"
        :final-voiceover-tone-options="finalVoiceoverToneOptions"
        :final-bgm-plan-options="finalBgmPlanOptions"
        :final-tts-provider="finalTtsProvider"
        :final-tts-voice-id="finalTtsVoiceId"
        :final-tts-speed="finalTtsSpeed"
        :final-tts-script="finalTtsScript"
        :final-tts-start-ms="finalTtsStartMs"
        :audio-options-loading="audioOptionsLoading"
        :tts-generating="ttsGenerating"
        :bgm-uploading="bgmUploading"
        :final-audio-mixing="finalAudioMixing"
        :audio-mix-controls="audioMixControls"
        :tts-voice-options="ttsVoiceOptions"
        :final-tts-timeline-segments="finalTtsTimelineSegments"
        :uploaded-final-bgm-audio="uploadedFinalBgmAudio"
        :audio-mix-preview-video="audioMixPreviewVideo"
        :audio-mix-delivery-video="audioMixDeliveryVideo"
        @update:finalVoiceoverMode="emit('update:finalVoiceoverMode', $event)"
        @update:finalVoiceoverTone="emit('update:finalVoiceoverTone', $event)"
        @update:finalBgmPlan="emit('update:finalBgmPlan', $event)"
        @update:finalTtsProvider="emit('update:finalTtsProvider', $event)"
        @update:finalTtsVoiceId="emit('update:finalTtsVoiceId', $event)"
        @update:finalTtsSpeed="emit('update:finalTtsSpeed', $event)"
        @update:finalTtsScript="emit('update:finalTtsScript', $event)"
        @update:finalTtsStartMs="emit('update:finalTtsStartMs', $event)"
        @update:ttsSegmentStartMs="emit('update:ttsSegmentStartMs', $event)"
        @update:audioMixControls="emit('update:audioMixControls', $event)"
        @generate-tts="emit('generate-tts')"
        @trigger-bgm-upload="emit('trigger-bgm-upload')"
        @generate-mix-preview="emit('generate-mix-preview')"
        @apply-preview-as-delivery="emit('apply-preview-as-delivery')"
        @clear-generated-tts="emit('clear-generated-tts')"
        @remove-tts-segment="emit('remove-tts-segment', $event)"
        @download-tts-segment="emit('download-tts-segment', $event)"
        @clear-uploaded-bgm="emit('clear-uploaded-bgm')"
        @download-uploaded-bgm="emit('download-uploaded-bgm')"
        @download-preview-video="emit('download-preview-video')"
        @open-audio-library="emit('open-audio-library', $event)"
      />
    </div>

    <section class="final-action-dock">
      <div class="final-action-dock__copy">
        <span class="final-side-card__eyebrow">交付动作</span>
        <h3>最后一步再执行动作</h3>
        <p>这里仅保留下载当前成片、上传人工终版和保存交付设置三个必要动作。</p>
      </div>
      <div class="final-action-dock__status">
        <span class="final-version-chip">{{ currentPlaybackLabel }}</span>
        <span class="final-version-chip final-version-chip--soft">{{ currentPlaybackStatusChip }}</span>
      </div>
      <div class="final-ops-grid final-ops-grid--dock">
        <el-button :disabled="!effectiveFinalVideoUrl" @click="emit('download-final')">下载当前成片</el-button>
        <el-button :loading="finalVideoUploading" @click="emit('trigger-upload')">上传人工终版</el-button>
        <el-button type="primary" :loading="finalWorkspaceSaving" @click="emit('save-workspace')">保存交付设置</el-button>
      </div>
    </section>

    <section v-if="pendingApproval" class="workspace-approval final-workspace-approval">
      <div class="workspace-approval__copy">
        <h3>成品确认</h3>
        <p>确认后完成当前项目；如果还需要人工修改，请上传成片并写清改动点。</p>
      </div>
      <div v-if="showSavedReviewFeedback" class="workspace-approval__saved-feedback">
        <span>已记录的审批意见</span>
        <p>{{ savedReviewFeedback }}</p>
      </div>
      <div v-if="approvalHistoryRecords.length" class="workspace-approval__history">
        <span>审批记录</span>
        <article v-for="record in approvalHistoryRecords" :key="record.id" class="workspace-approval__history-item">
          <div>
            <strong>{{ record.title }}</strong>
            <small>{{ record.timestamp || '刚刚' }}</small>
          </div>
          <p>{{ record.feedback }}</p>
        </article>
      </div>
      <el-input
        :model-value="reviewFeedback"
        type="textarea"
        :rows="4"
        maxlength="1000"
        show-word-limit
        :placeholder="reviewFeedbackPlaceholder"
        @update:model-value="emit('update:reviewFeedback', String($event))"
      />
      <div class="workspace-approval__actions">
        <el-button :loading="acting" :disabled="!canRequestRevision" @click="emit('request-revision')">{{ revisionActionLabel }}</el-button>
        <el-button type="primary" :loading="acting" :disabled="!canApproveCurrentStage" @click="emit('approve')">{{ approveButtonLabel }}</el-button>
      </div>
    </section>

    <section v-else class="workspace-approval workspace-approval--muted">
      <div class="workspace-approval__copy">
        <h3>当前状态</h3>
        <p>{{ sideGuidance }}</p>
      </div>
      <div v-if="showSavedReviewFeedback" class="workspace-approval__saved-feedback">
        <span>最近一次审批意见</span>
        <p>{{ savedReviewFeedback }}</p>
      </div>
      <div v-if="approvalHistoryRecords.length" class="workspace-approval__history">
        <span>审批记录</span>
        <article v-for="record in approvalHistoryRecords" :key="record.id" class="workspace-approval__history-item">
          <div>
            <strong>{{ record.title }}</strong>
            <small>{{ record.timestamp || '刚刚' }}</small>
          </div>
          <p>{{ record.feedback }}</p>
        </article>
      </div>
      <div v-if="showResumeAction" class="workspace-approval__actions">
        <el-button type="primary" :loading="acting" @click="emit('resume')">恢复并继续</el-button>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FinalAudioStudioCard from './FinalAudioStudioCard.vue'
import { getCurrentPlaybackLabel, getCurrentPlaybackStatusChip } from './finalDeliveryPresentation'

import type { AudioMixControlDraft, FinalAudioNarrationSegmentDraft, FinalVideoPlaybackSource, UploadedWorkspaceAudio, UploadedWorkspaceVideo } from './types'

const props = defineProps<{
  effectiveFinalVideoUrl: string
  systemFinalVideoUrl: string
  finalVideoPlayerError: string
  finalStageStatusLabel: string
  finalStageVideoMessage: string
  finalStageFacts: Array<{ label: string; value: string }>
  finalVideoUploading: boolean
  finalWorkspaceSaving: boolean
  uploadedFinalVideo: UploadedWorkspaceVideo | null
  audioMixPreviewVideo: UploadedWorkspaceVideo | null
  audioMixDeliveryVideo: UploadedWorkspaceVideo | null
  preferredFinalVideoSource: FinalVideoPlaybackSource
  finalVoiceoverMode: string
  finalVoiceoverTone: string
  finalBgmPlan: string
  finalVoiceoverModeOptions: ReadonlyArray<{ label: string; value: string }>
  finalVoiceoverToneOptions: ReadonlyArray<{ label: string; value: string }>
  finalBgmPlanOptions: ReadonlyArray<{ label: string; value: string }>
  finalTtsProvider: string
  finalTtsVoiceId: string
  finalTtsSpeed: number
  finalTtsScript: string
  finalTtsStartMs: number
  audioOptionsLoading: boolean
  ttsGenerating: boolean
  bgmUploading: boolean
  finalAudioMixing: boolean
  audioMixControls: AudioMixControlDraft
  ttsVoiceOptions: ReadonlyArray<{ label: string; value: string; description?: string | null }>
  finalTtsTimelineSegments: ReadonlyArray<FinalAudioNarrationSegmentDraft>
  uploadedFinalBgmAudio: UploadedWorkspaceAudio | null
  pendingApproval: boolean
  reviewFeedback: string
  savedReviewFeedback: string
  approvalHistoryRecords: Array<{ id: string; stage: string; stageLabel: string; feedback: string; timestamp: string; title: string }>
  acting: boolean
  canRequestRevision: boolean
  canApproveCurrentStage: boolean
  reviewFeedbackPlaceholder: string
  revisionActionLabel: string
  approveButtonLabel: string
  sideGuidance: string
  showResumeAction: boolean
}>()

const currentPlaybackLabel = computed(() => {
  return getCurrentPlaybackLabel({
    audioMixDeliveryVideo: props.audioMixDeliveryVideo,
    uploadedFinalVideo: props.uploadedFinalVideo,
  })
})

const currentPlaybackStatusChip = computed(() => {
  return getCurrentPlaybackStatusChip({
    audioMixDeliveryVideo: props.audioMixDeliveryVideo,
    uploadedFinalVideo: props.uploadedFinalVideo,
  })
})

const isPreviewApplied = computed(() => Boolean(
  props.audioMixPreviewVideo?.url
  && props.audioMixDeliveryVideo?.url
  && props.audioMixPreviewVideo.url === props.audioMixDeliveryVideo.url,
))

const isSystemPlaybackActive = computed(() => Boolean(
  props.systemFinalVideoUrl && props.effectiveFinalVideoUrl === props.systemFinalVideoUrl,
))

function normalizeFeedbackText(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim()
}

const showSavedReviewFeedback = computed(() => {
  if (props.approvalHistoryRecords.length) {
    return false
  }

  const feedback = normalizeFeedbackText(props.savedReviewFeedback)
  if (!feedback) {
    return false
  }

  const latestHistoryFeedback = normalizeFeedbackText(props.approvalHistoryRecords[0]?.feedback)
  return !latestHistoryFeedback || latestHistoryFeedback !== feedback
})

const previewSummaryText = computed(() => {
  if (isPreviewApplied.value) {
    return '这版声音预览已经被明确采纳，可以继续微调后重新生成新预览。'
  }
  if (props.audioMixPreviewVideo?.url) {
    return '预览已经生成，先试听旁白、BGM 和原声关系，再决定是否提升为交付版。'
  }
  return '先在下方准备旁白或 BGM，再生成第一版带画面的混音预览。'
})

const deliverySummaryText = computed(() => {
  if (props.audioMixDeliveryVideo?.url) {
    return '你已经有一版明确确认过的声音交付版，系统成片和人工终版都可以随时切回。'
  }
  if (props.uploadedFinalVideo?.url) {
    return '当前交付源是人工终版；如果想改回系统成片或声音交付版，可以在这里一键切换。'
  }
  if (props.systemFinalVideoUrl) {
    return '现在默认还是系统成片。只有你手动应用声音预览或上传人工终版后，交付版才会改变。'
  }
  return '当前还没有可交付的视频版本。'
})

const deliveryActions = computed(() => {
  const actions: Array<{ key: string; label: string; type: 'primary' | 'default'; onClick: () => void }> = []

  if (props.audioMixDeliveryVideo?.url && props.preferredFinalVideoSource !== 'audio_mix_delivery') {
    actions.push({
      key: 'audio',
      label: '切到声音交付版',
      type: 'primary',
      onClick: () => emit('use-audio-delivery'),
    })
  }

  if (props.uploadedFinalVideo?.url && props.preferredFinalVideoSource !== 'uploaded_final') {
    actions.push({
      key: 'uploaded',
      label: '切到人工终版',
      type: 'default',
      onClick: () => emit('use-uploaded-as-delivery'),
    })
  }

  if (props.systemFinalVideoUrl && props.preferredFinalVideoSource !== 'system') {
    actions.push({
      key: 'system',
      label: '回退到系统成片',
      type: 'default',
      onClick: () => emit('use-system-as-delivery'),
    })
  }

  return actions
})

const emit = defineEmits<{
  (event: 'download-final'): void
  (event: 'trigger-upload'): void
  (event: 'save-workspace'): void
  (event: 'final-video-loaded'): void
  (event: 'final-video-error'): void
  (event: 'update:finalVoiceoverMode', value: string): void
  (event: 'update:finalVoiceoverTone', value: string): void
  (event: 'update:finalBgmPlan', value: string): void
  (event: 'update:finalTtsProvider', value: string): void
  (event: 'update:finalTtsVoiceId', value: string): void
  (event: 'update:finalTtsSpeed', value: number): void
  (event: 'update:finalTtsScript', value: string): void
  (event: 'update:finalTtsStartMs', value: number): void
  (event: 'update:ttsSegmentStartMs', value: { id: string; startMs: number }): void
  (event: 'update:audioMixControls', value: AudioMixControlDraft): void
  (event: 'generate-tts'): void
  (event: 'trigger-bgm-upload'): void
  (event: 'generate-mix-preview'): void
  (event: 'apply-preview-as-delivery'): void
  (event: 'use-system-as-delivery'): void
  (event: 'use-uploaded-as-delivery'): void
  (event: 'use-audio-delivery'): void
  (event: 'clear-generated-tts'): void
  (event: 'remove-tts-segment', segmentId: string): void
  (event: 'download-tts-segment', segmentId: string): void
  (event: 'clear-uploaded-bgm'): void
  (event: 'download-uploaded-bgm'): void
  (event: 'download-preview-video'): void
  (event: 'open-audio-library', role: 'narration' | 'bgm'): void
  (event: 'update:reviewFeedback', value: string): void
  (event: 'request-revision'): void
  (event: 'approve'): void
  (event: 'resume'): void
}>()
</script>

<style scoped>
.final-review-shell,
.workspace-approval,
.workspace-approval__copy,
.workspace-approval__saved-feedback,
.final-stage-hero,
.final-stage-hero__media,
.final-review-workbench {
  display: grid;
  gap: 20px;
.workspace-approval__saved-feedback {
  gap: 8px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(245, 158, 11, 0.2);
  background: rgba(255, 251, 235, 0.9);
}

.workspace-approval__saved-feedback span,
.workspace-approval__saved-feedback p,
.workspace-approval__history span,
.workspace-approval__history-item p {
  margin: 0;
}

.workspace-approval__saved-feedback span {
  color: #b45309;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.workspace-approval__saved-feedback p {
  color: #7c2d12;
  line-height: 1.7;
}

.workspace-approval__history {
  display: grid;
  gap: 10px;
}

.workspace-approval__history span {
  color: #0f4c81;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.workspace-approval__history-item {
  display: grid;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(248, 250, 252, 0.82);
}

.workspace-approval__history-item div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
}

.workspace-approval__history-item strong {
  color: var(--final-ink);
  font-size: 14px;
}

.workspace-approval__history-item small {
  color: var(--final-muted);
  font-size: 12px;
}

.workspace-approval__history-item p {
  color: #334155;
  line-height: 1.7;
  white-space: pre-wrap;
}
}

.final-review-shell {
  --final-ink: #0f172a;
  --final-muted: #5f6b7d;
  --final-border: rgba(148, 163, 184, 0.2);
  --final-surface: rgba(255, 255, 255, 0.94);
  gap: 20px;
}

.block-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 220px);
  gap: 14px;
  align-items: center;
}

.block-header__copy {
  display: grid;
  gap: 6px;
}

.block-eyebrow,
.final-side-card__eyebrow {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 6px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.block-eyebrow {
  background: rgba(12, 74, 110, 0.08);
  color: #0f4c81;
}

.block-header h2,
.final-side-card h3,
.workspace-approval__copy h3 {
  margin: 0;
  color: var(--final-ink);
}

.block-header h2 {
  font-size: 28px;
  line-height: 1.08;
}

.block-tip,
.workspace-approval__copy p,
.final-action-card__step p,
.upload-summary-card p,
.final-review-player__error p,
.final-review-player__placeholder p {
  margin: 0;
  color: var(--final-muted);
  line-height: 1.7;
}

.block-header__status-card,
.final-side-card,
.workspace-approval,
.final-action-dock {
  padding: 20px;
  border-radius: 24px;
  border: 1px solid var(--final-border);
  background: var(--final-surface);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.05);
}

.block-header__status-card {
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 20px;
  background:
    radial-gradient(circle at top right, rgba(250, 204, 21, 0.14), transparent 34%),
    linear-gradient(160deg, rgba(255, 251, 235, 0.96), rgba(255, 255, 255, 0.92));
}

.block-header__status-card span,
.block-header__status-card small,
.final-action-card__step span,
.final-side-card__head small,
.final-review-player__badge {
  font-size: 12px;
}

.block-header__status-card span,
.final-side-card__head small {
  color: var(--final-muted);
}

.block-header__status-card strong {
  font-size: 18px;
  color: #9a3412;
}

.block-header__status-card small {
  color: #7c2d12;
  line-height: 1.45;
}

.final-stage-hero {
  grid-template-columns: 1fr;
}

.final-stage-hero__media {
  gap: 0;
  padding: 18px;
  border-radius: 32px;
  background:
    radial-gradient(circle at top left, rgba(125, 211, 252, 0.22), transparent 34%),
    radial-gradient(circle at bottom right, rgba(251, 191, 36, 0.18), transparent 24%),
    linear-gradient(160deg, rgba(241, 245, 249, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(191, 219, 254, 0.5);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.08);
}

.final-delivery-rail {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.delivery-step-card {
  display: grid;
  gap: 12px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.04);
}

.delivery-step-card--accent {
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.14), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(239, 246, 255, 0.94));
  border-color: rgba(96, 165, 250, 0.24);
}

.delivery-step-card__head {
  display: grid;
  gap: 6px;
}

.delivery-step-card__head strong {
  color: var(--final-ink);
}

.delivery-step-card__head p,
.delivery-step-card p {
  margin: 0;
  color: var(--final-muted);
  line-height: 1.7;
}

.delivery-step-card__step {
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(14, 116, 144, 0.08);
  color: #0f4c81;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.delivery-step-card__status,
.delivery-step-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.final-review-player {
  position: relative;
  min-height: 460px;
  border-radius: 28px;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a, #1e293b 62%, #334155);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.final-review-player__badge {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(12px);
  font-weight: 700;
}

.final-review-player video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.final-review-player__placeholder {
  min-height: 100%;
  display: grid;
  place-items: center;
  gap: 12px;
  padding: 36px;
  text-align: center;
  color: #e2e8f0;
}

.final-review-player__placeholder strong {
  display: block;
  color: #f8fafc;
  font-size: 30px;
}

.final-review-player__error {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  z-index: 2;
  display: grid;
  gap: 6px;
  padding: 16px 18px;
  border-radius: 18px;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.24);
  backdrop-filter: blur(12px);
}

.final-review-player__error strong {
  color: #f8fafc;
  font-size: 17px;
}

.final-side-card {
  display: grid;
  gap: 16px;
}

.final-side-card__head,
.final-action-card__head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: start;
}

.final-side-card__eyebrow {
  background: rgba(15, 118, 110, 0.08);
  color: #0f766e;
}

.final-side-card__helper {
  margin: 0;
  color: var(--final-muted);
  line-height: 1.7;
}

.final-version-card {
  background:
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.16), transparent 28%),
    linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(239, 246, 255, 0.94));
  box-shadow: 0 22px 44px rgba(14, 116, 144, 0.08);
}

.final-action-card__status {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  color: #0f4c81;
  font-weight: 700;
}

.final-action-card__summaries {
  display: grid;
  gap: 10px;
}

.upload-summary-card strong,
.final-stage-guide-card__list li {
  color: var(--final-ink);
}

.final-version-card__chips,
.final-action-dock__status {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.final-version-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
}

.final-version-chip--soft {
  background: rgba(56, 189, 248, 0.12);
  color: #0f4c81;
}

.final-version-chip--accent {
  background: rgba(20, 184, 166, 0.12);
  color: #0f766e;
}

.final-stage-guide-card {
  background:
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.14), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(240, 253, 250, 0.9));
}

.final-stage-guide-card__list {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 12px;
  color: var(--final-muted);
  line-height: 1.7;
}

.final-action-dock {
  display: grid;
  grid-template-columns: minmax(240px, 0.9fr) auto minmax(360px, 1.2fr);
  gap: 18px;
  align-items: center;
  background:
    radial-gradient(circle at top right, rgba(59, 130, 246, 0.14), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(241, 245, 249, 0.96));
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.06);
}

.final-action-dock__copy {
  display: grid;
  gap: 8px;
}

.final-action-dock__copy h3 {
  margin: 0;
  color: var(--final-ink);
}

.final-action-dock__copy p {
  margin: 0;
  color: var(--final-muted);
  line-height: 1.7;
}

.final-ops-grid--dock {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
}

.final-ops-grid--dock :deep(.el-button:nth-child(3)) {
  grid-column: auto;
}

.final-ops-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.upload-summary-card strong {
  display: block;
}

.upload-summary-card p {
  margin-top: 6px;
}

.final-ops-grid :deep(.el-button) {
  min-height: 44px;
}

.final-ops-grid :deep(.el-button:nth-child(3)) {
  grid-column: 1 / -1;
}

.upload-summary-card {
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.upload-summary-card--neutral {
  background: rgba(15, 23, 42, 0.04);
}

.upload-summary-card--final {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.18);
}

.upload-summary-card--accent {
  background: rgba(20, 184, 166, 0.08);
  border-color: rgba(20, 184, 166, 0.18);
}

.workspace-approval {
  background: linear-gradient(135deg, rgba(240, 253, 250, 0.94), rgba(255, 251, 235, 0.92));
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.06);
}

.workspace-approval--muted {
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.96));
}

.final-workspace-approval {
  background: rgba(255, 255, 255, 0.98);
}

.workspace-approval__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

@media (max-width: 1180px) {
  .block-header,
  .final-stage-hero,
  .final-delivery-rail,
  .final-ops-grid--dock,
  .final-action-dock {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .block-header h2 {
    font-size: 24px;
  }

  .final-review-player {
    min-height: 320px;
  }

  .final-review-player__error {
    left: 12px;
    right: 12px;
    bottom: 12px;
  }

  .final-action-card__head,
  .final-side-card__head {
    flex-direction: column;
    align-items: start;
  }

  .final-ops-grid {
    grid-template-columns: 1fr;
  }

  .final-ops-grid :deep(.el-button:nth-child(3)),
  .final-ops-grid--dock :deep(.el-button:nth-child(3)) {
    grid-column: auto;
  }
}
</style>
